






























import {
  Component,
  Prop,
  Mixins,
  Watch,
} from 'vue-property-decorator';
import { like, and } from '@/classes/pimcore-filtering/Filters';
import EntityHelperMixin from '@/mixins/EntityHelperMixin';
import NormalizerMixin from '@/mixins/NormalizerMixin';

@Component
export default class EntityFilter extends Mixins(EntityHelperMixin, NormalizerMixin) {
  @Prop({
    type: String,
    default: false,
  }) readonly filterEntity!: string

  @Prop({
    type: String,
    default: false,
  }) readonly fieldName!: string

  @Prop({
    type: Object,
    required: false,
    default: () => ({}),
  }) readonly variables!: any

  /** 'id-name' (по умолчанию, как было) или 'name-only' — только название без id. */
  @Prop({
    type: String,
    default: 'id-name',
  }) readonly labelMode!: string

  /** Сортировать пункты по названию (алфавит). По умолчанию — порядок ответа GraphQL. */
  @Prop({
    type: Boolean,
    default: false,
  }) readonly sortByName!: boolean

  /** Неинтерактивен (например, dirty-lock на /rates). */
  @Prop({
    type: Boolean,
    default: false,
  }) readonly disabled!: boolean

  /** Подсказка при наведении, когда disabled. */
  @Prop({
    type: String,
    default: '',
  }) readonly disabledHint!: string

  /** Начальное значение (приоритетнее localStorage, например из URL). */
  @Prop({
    type: [String, Number],
    default: '',
  }) readonly initialValue!: string | number

  /**
   * Ограничивать список Client выбранной платформой (F-Q6, страница /rates).
   * На страницах вроде ApplicationHR не включается — клиенты грузятся через backend filter.
   */
  @Prop({
    type: Boolean,
    default: false,
  }) readonly clientScopedByPlatform!: boolean

  /** id выбранной платформы — для фильтра Client показывать только клиентов этой платформы. */
  @Prop({
    type: [String, Number],
    default: '',
  }) readonly scopePlatformId!: string | number

  /** Доп. поля для introspection-запроса (например { Plants: { id: '' } } для Client). */
  @Prop({
    type: Object,
    default: () => ({}),
  }) readonly extraRequiredFields!: Record<string, any>

  /** Изменение токена принудительно сбрасывает значение (сброс клиента при смене платформы). */
  @Prop({
    type: Number,
    default: 0,
  }) readonly resetToken!: number

  loading = false;

  filterValue = '';

  items: Array<any> = [];

  get entity() {
    return this.filterEntity;
  }

  get capitalizedName() {
    return this.fieldName.charAt(0).toUpperCase() + this.fieldName.slice(1);
  }

  async beforeMount() {
    this.$bus.$on('clear-filters', (item: any) => {
      this.filterValue = '';
      this.setToStorage(this.fieldName, '');
    });
  }

  async mounted() {
    try {
      await this.fetchFilterItems();
    } catch (error) {
      // Listings/introspection may fail (local ESB 440, expired session, network) — must not block page.
      // eslint-disable-next-line no-console
      console.warn(`[EntityFilter] ${this.filterEntity}: failed to load filter items`, error);
      this.items = [];
      this.loading = false;
    }
    this.$emit('loaded');
  }

  beforeDestroy() {
    this.$bus.$off('clear-filters');
  }

  async fetchFilterItems() {
    this.loading = true;
    try {
      this.items = await this.loadEntityListing(
        this.entity,
        this.variables,
        'network-only',
        {
          maxDepth: 1,
          byObject: {
            ...this.getNormalizerRequiredFields(this.entity),
            ...this.extraRequiredFields,
          },
        },
      );
      this.hydrateValue();
    } finally {
      this.loading = false;
    }
  }

  /** Приоритет: URL (initialValue) > localStorage. Невалидный id — молча сброс (F-Q1). */
  hydrateValue() {
    const urlVal = this.initialValue !== '' && this.initialValue != null
      ? String(this.initialValue)
      : '';
    const storageVal = this.getFromStorage(this.fieldName);
    const candidate = urlVal !== '' ? urlVal : storageVal;

    if (candidate === '') {
      return;
    }

    if (this.isValidValue(candidate)) {
      this.filterValue = candidate;
      // URL перезаписывает localStorage (F-Q4); echo storage безвреден.
      this.setToStorage(this.fieldName, candidate);
      this.setFilter(candidate);
    } else {
      this.filterValue = '';
      this.setToStorage(this.fieldName, '');
      this.setFilter('');
    }
  }

  isValidValue(value: any): boolean {
    if (value === '' || value == null) {
      return false;
    }
    return this.items.some((v) => String(v.id) === String(value));
  }

  /** Принадлежит ли клиент выбранной платформе (F-Q6). */
  isValidForPlatform(value: any): boolean {
    if (!this.clientScopedByPlatform || this.filterEntity !== 'Client') {
      return true;
    }
    if (this.scopePlatformId === '' || this.scopePlatformId == null) {
      return false;
    }
    const item = this.items.find((v) => String(v.id) === String(value));
    if (!item || !Array.isArray(item.Plants)) {
      return false;
    }
    const pid = Number(this.scopePlatformId);
    return item.Plants.some((p: any) => Number(p?.id) === pid);
  }

  @Watch('scopePlatformId')
  onScopePlatformChange() {
    // Если выбранный клиент не относится к новой платформе — молча сбросить (F-Q1/F-Q6).
    if (!this.clientScopedByPlatform || this.filterEntity !== 'Client' || this.filterValue === '') {
      return;
    }
    if (!this.isValidForPlatform(this.filterValue)) {
      this.filterValue = '';
      this.setToStorage(this.fieldName, '');
      this.setFilter('');
    }
  }

  @Watch('resetToken')
  onResetToken() {
    // Принудительный сброс (смена платформы → сброс клиента, F-Q6).
    if (this.filterValue === '') {
      return;
    }
    this.filterValue = '';
    this.setToStorage(this.fieldName, '');
    this.setFilter('');
  }

  setFilter(value: any) {
    this.$emit('setFilter', this.entityFilter(value));
    this.$emit('change', value);
  }

  onSearch(value: any) {
    this.setToStorage(this.fieldName, value);
    this.setFilter(value);
    this.$emit('onSearch');
  }

  entityFilter(value: any) {
    if (!value) {
      return null;
    }
    return and(like(`${this.fieldName}__id`, value));
  }

  get filterItems() {
    let source = this.items;

    // Ф-Q6 (/rates): показываем только клиентов выбранной платформы; без платформы — пусто.
    if (this.clientScopedByPlatform && this.filterEntity === 'Client') {
      if (this.scopePlatformId === '' || this.scopePlatformId == null) {
        source = [];
      } else {
        const pid = Number(this.scopePlatformId);
        source = source.filter((v) => Array.isArray(v.Plants)
          && v.Plants.some((p: any) => Number(p?.id) === pid));
      }
    }

    const mapped = source.map((v) => {
      const displayName = this.normalizeValue(v);
      return {
        text: this.labelMode === 'name-only' ? `${displayName}` : `${v.id} - ${displayName}`,
        sortKey: displayName == null ? '' : String(displayName),
        value: v.id,
      };
    });

    if (this.sortByName) {
      // Эталон сортировки vue-front (OperationReport / PretensionsReport).
      mapped.sort((a, b) => a.sortKey.localeCompare(b.sortKey, undefined, {
        numeric: true,
        sensitivity: 'base',
      }));
    }

    return mapped;
  }
}

